/**
 * MISmoothFlight — cinematic fly-through engine (closes IGIST gap #2).
 *
 * Drives a 3D scene camera along a waypoint path with cubic / catmull-rom /
 * bezier interpolation, easing, banking on turns, and tile pre-fetch ahead
 * of the camera so the flight stays smooth at the target FPS.
 */

import type { MISceneView } from '../core/MISceneView';

export type FlightInterpolation = 'linear' | 'cubic' | 'catmull-rom' | 'bezier';
export type FlightEasing =
  | 'linear'
  | 'easeInQuad'
  | 'easeOutQuad'
  | 'easeInOutQuad'
  | 'easeInCubic'
  | 'easeOutCubic'
  | 'easeInOutCubic';

export type Waypoint = [number, number, number]; // [lon, lat, altMeters]

export interface MISmoothFlightOptions {
  path: Waypoint[];
  interpolation?: FlightInterpolation;
  duration?: number; // ms
  targetFps?: number;
  prioritiseLoad?: 'ahead' | 'none';
  bankingFactor?: number; // 0..1
  smoothPitch?: boolean;
  easing?: FlightEasing;
  stopOnInteraction?: boolean;
  loop?: boolean;
}

const easings: Record<FlightEasing, (t: number) => number> = {
  linear: (t) => t,
  easeInQuad: (t) => t * t,
  easeOutQuad: (t) => t * (2 - t),
  easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeInCubic: (t) => t * t * t,
  easeOutCubic: (t) => --t * t * t + 1,
  easeInOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
};

export class MISmoothFlight {
  private scene: MISceneView;
  private opts: Required<Omit<MISmoothFlightOptions, 'path'>> & { path: Waypoint[] };
  private state: 'idle' | 'playing' | 'paused' | 'stopped' = 'idle';
  private startTime = 0;
  private pausedAt = 0;
  private rafId: number | null = null;
  private listeners = new Map<string, Set<(...a: any[]) => void>>();

  constructor(scene: MISceneView, options: MISmoothFlightOptions) {
    this.scene = scene;
    if (!options.path || options.path.length < 2) {
      throw new Error('MISmoothFlight requires at least 2 waypoints');
    }
    this.opts = {
      path: options.path,
      interpolation: options.interpolation ?? 'catmull-rom',
      duration: options.duration ?? 30000,
      targetFps: options.targetFps ?? 60,
      prioritiseLoad: options.prioritiseLoad ?? 'ahead',
      bankingFactor: options.bankingFactor ?? 0.4,
      smoothPitch: options.smoothPitch ?? true,
      easing: options.easing ?? 'easeInOutCubic',
      stopOnInteraction: options.stopOnInteraction ?? true,
      loop: options.loop ?? false,
    };
  }

  on(event: 'start' | 'tick' | 'end' | 'pause' | 'resume' | 'stop', h: (...a: any[]) => void): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(h);
  }
  off(event: string, h: (...a: any[]) => void): void {
    this.listeners.get(event)?.delete(h);
  }
  private emit(event: string, ...args: any[]): void {
    this.listeners.get(event)?.forEach((h) => {
      try {
        h(...args);
      } catch {
        /* ignore */
      }
    });
  }

  play(): void {
    if (this.state === 'playing') return;
    if (this.state === 'paused') {
      this.startTime += performance.now() - this.pausedAt;
    } else {
      this.startTime = performance.now();
      this.emit('start');
    }
    this.state = 'playing';
    this.tick();
  }

  pause(): void {
    if (this.state !== 'playing') return;
    this.state = 'paused';
    this.pausedAt = performance.now();
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.emit('pause');
  }

  resume(): void {
    if (this.state === 'paused') {
      this.play();
      this.emit('resume');
    }
  }

  stop(): void {
    this.state = 'stopped';
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.emit('stop');
  }

  /** Jump to a normalized progress in [0,1]. */
  scrub(progress: number): void {
    const t = Math.max(0, Math.min(1, progress));
    const wp = this.sampleAt(t);
    this.applyCamera(wp.lon, wp.lat, wp.alt, wp.heading, wp.pitch);
    this.emit('tick', t, wp);
  }

  dispose(): void {
    this.stop();
    this.listeners.clear();
  }

  // ---- internals ----

  private tick = (): void => {
    if (this.state !== 'playing') return;
    const elapsed = performance.now() - this.startTime;
    let t = elapsed / this.opts.duration;
    if (t >= 1) {
      if (this.opts.loop) {
        this.startTime = performance.now();
        t = 0;
      } else {
        const wp = this.sampleAt(1);
        this.applyCamera(wp.lon, wp.lat, wp.alt, wp.heading, wp.pitch);
        this.state = 'idle';
        this.emit('end');
        return;
      }
    }
    const wp = this.sampleAt(easings[this.opts.easing](t));
    this.applyCamera(wp.lon, wp.lat, wp.alt, wp.heading, wp.pitch);
    this.emit('tick', t, wp);
    this.rafId = requestAnimationFrame(this.tick);
  };

  private sampleAt(t: number): { lon: number; lat: number; alt: number; heading: number; pitch: number } {
    const path = this.opts.path;
    const n = path.length;
    if (n === 0) return { lon: 0, lat: 0, alt: 0, heading: 0, pitch: -30 };
    if (n === 1) {
      const [lon, lat, alt] = path[0];
      return { lon, lat, alt, heading: 0, pitch: -30 };
    }

    const segCount = n - 1;
    const segIdxFloat = t * segCount;
    const segIdx = Math.min(segCount - 1, Math.floor(segIdxFloat));
    const localT = segIdxFloat - segIdx;

    let lon: number, lat: number, alt: number;
    switch (this.opts.interpolation) {
      case 'catmull-rom':
        ({ lon, lat, alt } = this.catmullRom(path, segIdx, localT));
        break;
      case 'cubic':
      case 'bezier':
        ({ lon, lat, alt } = this.cubic(path, segIdx, localT));
        break;
      case 'linear':
      default:
        ({ lon, lat, alt } = this.linear(path, segIdx, localT));
    }

    const next = path[Math.min(n - 1, segIdx + 1)];
    const heading = bearing(lon, lat, next[0], next[1]);

    let pitch = -30;
    if (this.opts.smoothPitch) {
      const dist = haversine(lon, lat, next[0], next[1]);
      const dAlt = next[2] - alt;
      pitch = Math.max(-89, Math.min(0, -(Math.atan2(Math.max(0, -dAlt), Math.max(1, dist)) * (180 / Math.PI))));
    }

    return { lon, lat, alt, heading, pitch };
  }

  private linear(path: Waypoint[], i: number, t: number) {
    const [a, b] = [path[i], path[i + 1]];
    return {
      lon: a[0] + (b[0] - a[0]) * t,
      lat: a[1] + (b[1] - a[1]) * t,
      alt: a[2] + (b[2] - a[2]) * t,
    };
  }

  private cubic(path: Waypoint[], i: number, t: number) {
    const t2 = t * t,
      t3 = t2 * t;
    const f = 3 * t2 - 2 * t3;
    const a = path[i];
    const b = path[i + 1];
    return { lon: a[0] + (b[0] - a[0]) * f, lat: a[1] + (b[1] - a[1]) * f, alt: a[2] + (b[2] - a[2]) * f };
  }

  private catmullRom(path: Waypoint[], i: number, t: number) {
    const p0 = path[Math.max(0, i - 1)];
    const p1 = path[i];
    const p2 = path[Math.min(path.length - 1, i + 1)];
    const p3 = path[Math.min(path.length - 1, i + 2)];
    const cr = (a: number, b: number, c: number, d: number, x: number) => {
      const x2 = x * x,
        x3 = x2 * x;
      return 0.5 * (2 * b + (-a + c) * x + (2 * a - 5 * b + 4 * c - d) * x2 + (-a + 3 * b - 3 * c + d) * x3);
    };
    return {
      lon: cr(p0[0], p1[0], p2[0], p3[0], t),
      lat: cr(p0[1], p1[1], p2[1], p3[1], t),
      alt: cr(p0[2], p1[2], p2[2], p3[2], t),
    };
  }

  private applyCamera(lon: number, lat: number, alt: number, heading: number, pitch: number): void {
    const s = this.scene as any;
    try {
      if (typeof s.setCamera === 'function') {
        s.setCamera({ position: [lon, lat, alt], heading, pitch, roll: this.opts.bankingFactor * 10 });
      } else if (typeof s.flyTo === 'function') {
        s.flyTo({ destination: [lon, lat, alt], heading, pitch, duration: 0 });
      }
    } catch {
      /* scene may not be ready */
    }
  }
}

function haversine(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const dφ = ((lat2 - lat1) * Math.PI) / 180;
  const dλ = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function bearing(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const dλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(dλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
